import { css } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { CheckboxAlignButtonEnum } from '@inter-ikea-kompis/component-checkbox';
import RadioLayoutEnum from '../../enums/RadioLayoutEnum.js';
const INPUT_SIZE = 44;
const INPUT_POSITION_OFFSET = 10;
export default class RadioButtonStyle {
    /**
     * Returns styles.
     *
     * @param options Options object.
     * @param options.isDisabled Viewport layout.
     * @param options.isChecked "true" if checked.
     * @param options.direction Direction.
     * @param options.layout Layout.
     * @param options.theme Theme.
     * @returns Styles.
     */
    static getStyle(options) {
        const { isDisabled } = options;
        const { spacing, color, motion, border } = options.theme;
        return css `
			${FontStyle.getStyle({
            type: ThemeFontStyleTypeEnum.bodyMedium,
            theme: options.theme
        })}
			:host {
				display: inline-block;
				color: ${color.neutral.grey700};
			}
			input[type='number'] {
				/* remove default arrows in input type=number in Firefox*/
				-moz-appearance: textfield;
			}
			input[type='radio'] {
				position: absolute;
				width: ${INPUT_SIZE / 16}rem;
				height: ${INPUT_SIZE / 16}rem;
				font-size: inherit;
				-webkit-appearance: none;
				opacity: 0;
				margin: 0;
				outline: 0;
				padding: 0;
				cursor: pointer;
				top: ${'-' + INPUT_POSITION_OFFSET / 16 + 'rem'};
				${this.getRTLStyling(options)}
			}
			input:active {
				border-color: ${color.semantic.positive};
			}
			kompis-text {
				font-size: inherit;
				line-height: inherit;
				color: currentColor;
				display: inline-flex;
				transition-property: color;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
			}
			.container {
				display: flex;
				flex-direction: ${options.alignButton === CheckboxAlignButtonEnum.left
            ? 'row'
            : 'row-reverse'};
				align-items: flex-start;
				position: relative;
				cursor: ${isDisabled ? 'default' : 'pointer'};
				outline: none;
			}
			.container label {
				margin-top: ${2 / 16}rem;
			}
			.outline {
				position: absolute;
				width: ${spacing.space200 / 16}rem;
				height: ${spacing.space200 / 16}rem;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				border-radius: ${border.radius.radius64 / 16}rem;
				transition-property: utline, border, opacity;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
				opacity: ${options.hasFocus ? 1 : 0};
			}
			.radioSymbol {
				width: 1.5rem;
				height: 1.5rem;
				display: flex;
				align-items: center;
				justify-content: center;
				background: ${color.neutral.white};
				border-width: ${1 / 16}rem;
				border-style: solid;
				border-color: ${color.neutral.grey500};
				border-radius: ${border.radius.radius64 / 16}rem;
				flex-shrink: 0;
				outline: none;
				transition-property: color, border, width, height;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
			}
			.radioSymbol:after {
				content: '';
				display: block;
				width: ${spacing.space50 / 16}rem;
				height: ${spacing.space50 / 16}rem;
				border-radius: ${border.radius.radius64 / 16}rem;
				transition-property: color, width, height;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
			}
			.container:hover .radioSymbol {
				border-color: ${color.neutral.grey900};
			}
			.divider {
				width: ${spacing.space100 / 16}rem;
				flex-shrink: 0;
			}
			.radioButtonContainer {
				position: relative;
				width: ${spacing.space150 / 16}rem;
				height: ${spacing.space150 / 16}rem;
				outline: none;
			}

			${options.isChecked ? this.getChecked(options) : ''}
			${this.getFocusStyle(options)}
			${!options.isDisabled ? this.getHoverStyle(options) : ''}
			${options.layout === RadioLayoutEnum.subtle ? this.getSubtleStyle(options) : ''}
			${options.isDisabled ? this.getDisabled(options) : ''}
			${this.checkHover(options)}
		`;
    }
    /**
     * Returns css rules for hoverStyle.
     *
     * @param options Options.
     * @param options.theme Theme.
     * @returns Css cssrule.
     */
    static getHoverStyle(options) {
        const { color, spacing, border } = options.theme;
        if (options.hasFocus || options.isChecked) {
            return '';
        }
        return css `
			/* Prevents hover effect on touch. */
			@media (-moz-touch-enabled: 0), (pointer: fine) {
				.container:hover > .radioButtonContainer > .radioSymbol:after {
					content: '';
					display: block;
					background: ${color.neutral.grey200};
					border-color: ${color.neutral.grey900};
					border-radius: ${border.radius.radius64 / 16}rem;
					width: ${spacing.space50 / 16}rem;
					height: ${spacing.space50 / 16}rem;
				}
				.container:hover > .radioSymbol {
					border-color: ${color.neutral.grey900};
				}
				.radioSymbol:hover:after {
					background: ${color.neutral.grey200};
					border-radius: ${border.radius.radius64 / 16}rem;
				}
				.radioSymbol:focus {
					border-color: ${color.component.primaryHover};
				}
			}
		`;
    }
    /**
     * Returns css rules for Subtle layout Style.
     *
     * @param options Options.
     * @param options.theme Theme.
     * @returns Styles.
     */
    static getSubtleStyle(options) {
        if (!options.isChecked) {
            return '';
        }
        const { color } = options.theme;
        return css `
			.radioSymbol {
				border-color: ${options.hasFocus ? color.neutral.grey500 : color.neutral.grey900};
			}
			.radioSymbol:after {
				background: ${color.neutral.grey900};
			}
		`;
    }
    /**
     * Returns styles.
     *
     * @param options Options object.
     * @param options.isDisabled Viewport layout.
     * @param options.isChecked "true" if checked.
     * @param options.theme Theme.
     * @returns Styles.
     */
    static getChecked(options) {
        const { color, border } = options.theme;
        return css `
			.radioSymbol {
				border-color: ${color.accent.primaryAccent};
			}
			.radioSymbol:after {
				background: ${color.accent.primaryAccent};
				border-radius: ${border.radius.radius64 / 16}rem;
				content: '';
				width: 1rem;
				height: 1rem;
				display: block;
			}
		`;
    }
    /**
     * Style for button disabled.
     *
     * @param options Options.
     * @param options.theme Theme.
     * @param options.disabled ...
     * @returns Style for button disabled.
     */
    static getDisabled(options) {
        const { color } = options.theme;
        return css `
			.radioSymbol {
				border-color: ${color.disabled.default};
			}
			.radioSymbol:after {
				background: ${color.disabled.default};
			}
			kompis-text {
				color: ${color.disabled.default};
				pointer-events: none;
			}
		`;
    }
    /**
     * Returns the style for focusing the button.
     *
     * @param options Options.
     * @returns Styles.
     */
    static getFocusStyle(options) {
        if (!options.hasFocus) {
            return '';
        }
        return css `
			${this.getOutlineStyle(options)}
		`;
    }
    /**
     * Returns the outline style when focusing.
     *
     * @param options Options.
     * @returns Styles.
     */
    static getOutlineStyle(options) {
        const { focusOutline } = options.theme;
        if (!options.hasFocus) {
            return '';
        }
        return css `
			.outline {
				box-shadow: 0 0 0 ${1 / 16}rem ${focusOutline.color};
				left: -${focusOutline.offset / 16}rem;
				top: -${focusOutline.offset / 16}rem;
				right: -${focusOutline.offset / 16}rem;
				bottom: -${focusOutline.offset / 16}rem;
			}
		`;
    }
    /**
     * Returns styling for options.hover.
     *
     * @param options ...
     * @returns Styles.
     */
    static checkHover(options) {
        const { color, spacing, border } = options.theme;
        if (options.hover && !options.isChecked) {
            return css `
				/* Prevents hover effect on touch. */
				@media (-moz-touch-enabled: 0), (pointer: fine) {
					.container > .radioSymbol:after {
						content: '';
						display: block;
						background: ${color.neutral.grey200};
						border-color: ${color.neutral.grey900};
						width: ${spacing.space50 / 16}rem;
						height: ${spacing.space50 / 16}rem;
					}
					.container > .radioSymbol {
						border-color: ${color.neutral.grey900};
					}
					.radioSymbol:after {
						content: '';
						display: block;
						background: ${color.neutral.grey200};
						width: ${spacing.space50 / 16}rem;
						height: ${spacing.space50 / 16}rem;
						border-radius: ${border.radius.radius64 / 16}rem;
					}
					.radioSymbol:focus {
						border-color: ${color.component.primaryHover};
					}
				}
			`;
        }
        return '';
    }
    /**
     * Returns RTL styling for input element.
     *
     * @param options ...
     * @returns Styles.
     */
    static getRTLStyling(options) {
        if (options.direction === 'rtl') {
            if (options.alignButton === CheckboxAlignButtonEnum.right) {
                return css `
					right: ${'-' + INPUT_POSITION_OFFSET / 16 + 'rem'};
					left: auto;
				`;
            }
            else {
                return css `
					right: auto;
					left: ${'-' + INPUT_POSITION_OFFSET / 16 + 'rem'};
				`;
            }
        }
        else {
            if (options.alignButton === CheckboxAlignButtonEnum.right) {
                return css `
					right: ${'-' + INPUT_POSITION_OFFSET / 16 + 'rem'};
					left: auto;
				`;
            }
            else {
                return css `
					right: auto;
					left: ${'-' + INPUT_POSITION_OFFSET / 16 + 'rem'};
				`;
            }
        }
    }
}
//# sourceMappingURL=RadioButtonStyle.js.map